// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, Alert, Grid, Select, MenuItem, 
  InputLabel, FormControl, IconButton, Stack, Tooltip, Switch, Autocomplete
} from '@mui/material';
import { 
  Add as AddIcon, Delete as DeleteIcon, Badge as BadgeIcon, VpnKey as VpnKeyIcon, 
  CheckCircle as CheckCircleIcon, Refresh as RefreshIcon, Edit as EditIcon
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import { format } from 'date-fns';
import { 
  FlightTakeoff as LeaveIcon, 
  PriceCheck as PaymentIcon,
  Update as AccrualIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon
} from '@mui/icons-material';

const HRPage = () => {
  const [staff, setStaff] = useState<any[]>([]);
  const [positions, setPositions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [leaveRequests, setLeaveRequests] = useState<any[]>([]);
  const [loadingAction, setLoadingAction] = useState(false);
  
  // Onboarding Dialog
  const [openOnboardDialog, setOpenOnboardDialog] = useState(false);
  const INITIAL_ONBOARD_DATA = {
    name: '', email: '', password: '', department: 'Academic', position: 'Teacher',
    contractType: 'PERMANENT', dateOfJoining: new Date().toISOString().split('T')[0],
    personalEmail: '', contactNumber: '', address: '', nationalId: '',
    gender: 'MALE', nextOfKinName: '', nextOfKinContact: '', dependentsCount: 0,
    grantAccess: false, systemRole: 'STAFF',
    annualLeaveBalance: 0, 
    sickLeaveFullBalance: 90, 
    sickLeaveHalfBalance: 90, 
    specialLeaveBalance: 12,
    maternityLeaveBalance: 98,
    preferredPaymentMethod: 'CASH',
    bankName: '',
    bankAccount: '',
    mobileWalletNo: ''
  };
  const [onboardData, setOnboardData] = useState(INITIAL_ONBOARD_DATA);

  // Edit Dialog
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingStaffId, setEditingStaffId] = useState<string | null>(null);
  const [editData, setEditData] = useState(INITIAL_ONBOARD_DATA);

  // Grant Access Dialog
  const [openGrantDialog, setOpenGrantDialog] = useState(false);
  const [grantingStaff, setGrantingStaff] = useState<any>(null);
  const [grantData, setGrantData] = useState({ email: '', password: '', systemRole: 'STAFF' });

  // Leave Record Dialog
  const [openLeaveRecordDialog, setOpenLeaveRecordDialog] = useState(false);
  const [leaveRecordData, setLeaveRecordData] = useState({ staffId: '', leaveType: 'ANNUAL', startDate: '', endDate: '', daysTaken: 0, reason: '' });

  // Auto-calculate days for leave requests
  useEffect(() => {
    if (leaveRecordData.startDate && leaveRecordData.endDate) {
      const start = new Date(leaveRecordData.startDate);
      const end = new Date(leaveRecordData.endDate);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      setLeaveRecordData(prev => ({ ...prev, daysTaken: diffDays }));
    }
  }, [leaveRecordData.startDate, leaveRecordData.endDate]);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      const headers = { Authorization: `Bearer ${token}` };
      
      const res = await axios.get(getApiUrl('hr/directory'), { headers });
      setStaff(res.data);
      
      const leaveRes = await axios.get(getApiUrl('hr/leave/requests'), { headers });
      setLeaveRequests(leaveRes.data);

      const starterPositions = ['Principal', 'Deputy Principal', 'Head of Department', 'Senior Teacher', 'Teacher', 'Accountant', 'Bursar', 'Driver', 'General Hand', 'Security Guard'];
      try {
          const posRes = await axios.get(getApiUrl('hr/positions'), { headers });
          const dbPositions = Array.isArray(posRes.data) ? posRes.data : [];
          const uniqueList = Array.from(new Set([...dbPositions, ...starterPositions]));
          setPositions(uniqueList);
      } catch (e) {
          console.error("Failed to fetch positions:", e);
          setPositions(starterPositions);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load HR details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, []);

  const handleOnboard = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const userInfo = localStorage.getItem('userInfo');
      const token = userInfo ? JSON.parse(userInfo).token : null;
      await axios.post(getApiUrl('hr/onboard'), onboardData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(`Successfully enrolled ${onboardData.name}. Welcome email sent to ${onboardData.personalEmail}`);
      setOpenOnboardDialog(false);
      fetchStaff();
      setOnboardData(INITIAL_ONBOARD_DATA);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to onboard staff');
    }
  };

  const handleResendWelcome = async (id: string) => {
      try {
          const userInfo = localStorage.getItem('userInfo');
          const token = userInfo ? JSON.parse(userInfo).token : null;
          await axios.post(getApiUrl(`hr/resend-welcome/${id}`), {}, {
              headers: { Authorization: `Bearer ${token}` }
          });
          setSuccess('Welcome pack resent successfully to all registered emails.');
      } catch (err: any) {
          setError(err.response?.data?.message || 'Failed to resend email');
      }
  };

  const handleGrantAccessFinal = async (e: React.FormEvent) => {
      e.preventDefault();
      try {
          const userInfo = localStorage.getItem('userInfo');
          const token = userInfo ? JSON.parse(userInfo).token : null;
          await axios.post(getApiUrl(`hr/grant-access/${grantingStaff.id}`), grantData, {
             headers: { Authorization: `Bearer ${token}` }
          });
          setSuccess('System access granted. Activation email sent.');
          setOpenGrantDialog(false);
          fetchStaff();
      } catch (err: any) {
          setError(err.response?.data?.message || 'Failed to grant access');
      }
  };

  const handleTerminate = async (id: string, name: string) => {
      if(!window.confirm(`Are you extremely sure you wish to terminate ${name}? Access will be revoked.`)) return;
      try {
          const userInfo = localStorage.getItem('userInfo');
          const token = userInfo ? JSON.parse(userInfo).token : null;
          await axios.delete(getApiUrl(`hr/terminate/${id}`), {
             headers: { Authorization: `Bearer ${token}` }
          });
          fetchStaff();
      } catch (err: any) {
          setError(err.response?.data?.message || 'Failed to terminate staff');
      }
  };

  const handleEditClick = (employee: any) => {
      setEditingStaffId(employee.id);
      setEditData({
          name: employee.user?.name || employee.name || '',
          email: employee.user?.email || '',
          department: employee.department || 'Academic',
          position: employee.position || 'Teacher',
          contractType: employee.contractType || 'PERMANENT',
          dateOfJoining: employee.dateOfJoining ? new Date(employee.dateOfJoining).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          personalEmail: employee.personalEmail || '',
          contactNumber: employee.contactNumber || '',
          address: employee.address || '',
          nationalId: employee.nationalId || '',
          gender: employee.gender || 'MALE',
          nextOfKinName: employee.nextOfKinName || '',
          nextOfKinContact: employee.nextOfKinContact || '',
          dependentsCount: employee.dependentsCount || 0,
          grantAccess: !!employee.userId,
          systemRole: employee.user?.role || 'STAFF',
          annualLeaveBalance: employee.annualLeaveBalance || 0,
          sickLeaveFullBalance: employee.sickLeaveFullBalance || 0,
          sickLeaveHalfBalance: employee.sickLeaveHalfBalance || 0,
          specialLeaveBalance: employee.specialLeaveBalance || 0,
          maternityLeaveBalance: employee.maternityLeaveBalance || 0,
          preferredPaymentMethod: employee.preferredPaymentMethod || 'CASH',
          bankName: employee.bankName || '',
          bankAccount: employee.bankAccount || '',
          mobileWalletNo: employee.mobileWalletNo || ''
      });
      setOpenEditDialog(true);
  };

  const handleUpdateStaff = async (e: React.FormEvent) => {
      e.preventDefault();
      try {
          const userInfo = localStorage.getItem('userInfo');
          const token = userInfo ? JSON.parse(userInfo).token : null;
          await axios.put(getApiUrl(`hr/${editingStaffId}`), editData, {
              headers: { Authorization: `Bearer ${token}` }
          });
          setSuccess(`Successfully updated ${editData.name}.`);
          setOpenEditDialog(false);
          fetchStaff();
      } catch (err: any) {
          setError(err.response?.data?.message || 'Failed to update staff');
      }
  };

  const handleAccrueLeave = async () => {
    if(!window.confirm("This will add monthly leave days (1.75 days Annual) to all staff. Proceed?")) return;
    try {
        setLoadingAction(true);
        const userInfo = localStorage.getItem('userInfo');
        const token = userInfo ? JSON.parse(userInfo).token : null;
        await axios.post(getApiUrl('hr/leave/accrue'), {}, {
            headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Monthly leave accrual processed successfully for all personnel.');
        fetchStaff();
    } catch (err: any) {
        setError(err.response?.data?.message || 'Accrual failed');
    } finally {
        setLoadingAction(false);
    }
  };

  const handleLeaveStatusUpdate = async (id: string, status: string) => {
      try {
          setLoadingAction(true);
          const userInfo = localStorage.getItem('userInfo');
          const token = userInfo ? JSON.parse(userInfo).token : null;
          const user = JSON.parse(userInfo);
          await axios.put(getApiUrl(`hr/leave/requests/${id}/status`), { status, approvedBy: user.name }, {
              headers: { Authorization: `Bearer ${token}` }
          });
          setSuccess(`Leave request ${status.toLowerCase()} successfully.`);
          fetchStaff();
      } catch (err: any) {
          setError(err.response?.data?.message || 'Status update failed');
      } finally {
          setLoadingAction(false);
      }
  };

  const handleRecordLeaveSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
        setLoadingAction(true);
        const userInfo = localStorage.getItem('userInfo');
        const token = userInfo ? JSON.parse(userInfo).token : null;
        await axios.post(getApiUrl('hr/leave/requests'), leaveRecordData, {
            headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('Leave request recorded successfully');
        setOpenLeaveRecordDialog(false);
        setLeaveRecordData({ staffId: '', leaveType: 'ANNUAL', startDate: '', endDate: '', daysTaken: 0, reason: '' });
        fetchStaff();
    } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to record leave');
    } finally {
        setLoadingAction(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 2, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
            <Typography variant="h4" fontWeight={900} color="#1A202C">Personnel Hub</Typography>
            <Typography variant="subtitle1" color="text.secondary">Enroll workers and manage selective secure system access.</Typography>
        </Box>
        <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<AccrualIcon />} onClick={handleAccrueLeave} disabled={loadingAction}>Run Monthly Accrual</Button>
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setOpenOnboardDialog(true)} sx={{ borderRadius: '8px', fontWeight: 800, px: 3, py: 1 }}>Hire New Personnel</Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 4, borderRadius: '12px', fontWeight: 600 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 4, borderRadius: '12px', fontWeight: 600 }}>{success}</Alert>}

      <Paper sx={{ mb: 4, borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: 'none', overflow: 'hidden' }}>
          <Stack direction="row" sx={{ borderBottom: '1px solid #E2E8F0', bgcolor: '#F8FAFC' }}>
              <Button onClick={() => setTabIndex(0)} sx={{ px: 4, py: 2, borderRadius: 0, fontWeight: 800, borderBottom: tabIndex === 0 ? '3px solid #1a237e' : 'none', color: tabIndex === 0 ? '#1a237e' : 'text.secondary' }}>Personnel Directory</Button>
              <Button onClick={() => setTabIndex(1)} sx={{ px: 4, py: 2, borderRadius: 0, fontWeight: 800, borderBottom: tabIndex === 1 ? '3px solid #1a237e' : 'none', color: tabIndex === 1 ? '#1a237e' : 'text.secondary' }}>Leave Management</Button>
          </Stack>
      </Paper>

      {tabIndex === 0 && (
      <TableContainer component={Paper} sx={{ borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8FAFC' }}>
              <TableCell sx={{ fontWeight: 800 }}>Employee ID</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Name & Official Email</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Contact Info</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Leave Balances</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Status / Role</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800 }}>Account Control</TableCell>
              <TableCell align="center" sx={{ fontWeight: 800 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {staff.map((employee) => (
              <TableRow key={employee.id} hover>
                <TableCell sx={{ fontWeight: 800, color: '#2B6CB0' }}>{employee.employeeNo}</TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight={800}>{employee.user?.name || employee.name || 'Employee'}</Typography>
                  {employee.userId ? (
                      <Typography variant="caption" color="primary" fontWeight={700}>{employee.user?.email}</Typography>
                  ) : (
                      <Typography variant="caption" color="text.disabled">No System Account</Typography>
                  )}
                </TableCell>
                <TableCell>
                   <Typography variant="caption" display="block" fontWeight={700}>{employee.contactNumber || 'N/A'}</Typography>
                   <Typography variant="caption" display="block" color="text.secondary">{employee.personalEmail || 'N/A'}</Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="caption" display="block" fontWeight={800} color="#38A169">Annual: {employee.annualLeaveBalance || 0}d</Typography>
                    <Typography variant="caption" display="block" fontWeight={800} color="#3182CE">Sick(F): {employee.sickLeaveFullBalance || 0}d</Typography>
                    <Typography variant="caption" display="block" fontWeight={800} color="#D69E2E">Special: {employee.specialLeaveBalance || 0}d</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={employee.position} size="small" sx={{ mb: 0.5, borderRadius: '6px', bgcolor: '#f0f4f8', fontWeight: 700 }} />
                  <br />
                  {employee.userId ? (
                      <Tooltip title="Has System Credentials"><Chip label={employee.user?.role} size="small" color="success" variant="outlined" sx={{ borderStyle: 'dashed', height: 20, fontSize: 10, fontWeight: 800 }} /></Tooltip>
                  ) : (
                      <Tooltip title="HR Record Only"><Chip label="OFFLINE" size="small" variant="outlined" sx={{ borderStyle: 'dotted', height: 20, fontSize: 10 }} /></Tooltip>
                  )}
                </TableCell>
                <TableCell align="center">
                    {!employee.userId ? (
                        <Button size="small" startIcon={<VpnKeyIcon />} variant="outlined" color="primary" onClick={() => { setGrantingStaff(employee); setOpenGrantDialog(true); }} sx={{ borderRadius: '6px', fontWeight: 800, fontSize: 11 }}>
                            Grant Access
                        </Button>
                    ) : (
                        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                            <Tooltip title="Account Active"><CheckCircleIcon color="success" fontSize="small" /></Tooltip>
                            <Tooltip title="Resend Welcome Email"><IconButton size="small" onClick={() => handleResendWelcome(employee.id)} color="primary"><RefreshIcon fontSize="small" /></IconButton></Tooltip>
                        </Box>
                    )}
                </TableCell>
                <TableCell align="center">
                    <Stack direction="row" justifyContent="center" spacing={1}>
                        <Tooltip title="Edit Profile">
                            <IconButton color="primary" size="small" onClick={() => handleEditClick(employee)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Terminate Record">
                            <IconButton color="error" size="small" onClick={() => handleTerminate(employee.id, employee.user?.name || employee.name)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {tabIndex === 1 && (
          <Box>
            <Box display="flex" justifyContent="flex-end" mb={3} gap={2}>
                <Button variant="outlined" color="primary" startIcon={<RefreshIcon />} onClick={handleAccrueLeave} disabled={loadingAction} sx={{ borderRadius: '10px', fontWeight: 800 }}>
                    Run Monthly Accrual
                </Button>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setOpenLeaveRecordDialog(true)} sx={{ borderRadius: '10px', fontWeight: 800 }}>
                    Record Leave Request
                </Button>
            </Box>
            <TableContainer component={Paper} sx={{ borderRadius: '16px', border: '1px solid #E2E8F0', boxShadow: 'none' }}>
              <Table>
                  <TableHead>
                      <TableRow sx={{ bgcolor: '#f8fafc' }}>
                          <TableCell sx={{ fontWeight: 800 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>Staff Member</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>Period</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>Days</TableCell>
                          <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800 }}>Control</TableCell>
                      </TableRow>
                  </TableHead>
                  <TableBody>
                      {leaveRequests.map((req) => (
                          <TableRow key={req.id} hover>
                              <TableCell sx={{ fontWeight: 600 }}>{format(new Date(req.createdAt), 'dd MMM yyyy')}</TableCell>
                              <TableCell>
                                  <Typography variant="subtitle2" fontWeight={800}>{req.staff?.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">{req.staff?.employeeNo}</Typography>
                              </TableCell>
                              <TableCell><Chip label={req.leaveType} size="small" variant="outlined" sx={{ fontWeight: 800, fontSize: 10 }} /></TableCell>
                              <TableCell sx={{ fontSize: 13 }}>{format(new Date(req.startDate), 'dd MMM')} - {format(new Date(req.endDate), 'dd MMM')}</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>{req.daysTaken} days</TableCell>
                              <TableCell>
                                  <Chip 
                                    label={req.status} 
                                    size="small" 
                                    color={req.status === 'APPROVED' ? 'success' : req.status === 'REJECTED' ? 'error' : 'warning'} 
                                    sx={{ fontWeight: 900, borderRadius: '4px' }} 
                                  />
                              </TableCell>
                              <TableCell align="right">
                                  {req.status === 'PENDING' && (
                                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                                          <IconButton onClick={() => handleLeaveStatusUpdate(req.id, 'APPROVED')} color="success" size="small" disabled={loadingAction}><ApproveIcon /></IconButton>
                                          <IconButton onClick={() => handleLeaveStatusUpdate(req.id, 'REJECTED')} color="error" size="small" disabled={loadingAction}><RejectIcon /></IconButton>
                                      </Stack>
                                  )}
                              </TableCell>
                          </TableRow>
                      ))}
                      {leaveRequests.length === 0 && (
                          <TableRow><TableCell colSpan={7} sx={{ py: 10, textAlign: 'center' }}><Typography color="text.secondary">No leave records registered.</Typography></TableCell></TableRow>
                      )}
                  </TableBody>
              </Table>
          </TableContainer>
          </Box>
      )}

      {/* 1. ONBOARDING DIALOG */}
      <Dialog open={openOnboardDialog} onClose={() => setOpenOnboardDialog(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '24px', maxHeight: '90vh' } }}>
        <DialogTitle sx={{ px: 4, py: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E2E8F0', background: 'linear-gradient(135deg, #1a237e 0%, #263b7c 100%)', color: 'white' }}>
          <Box>
            <Typography variant="h5" fontWeight={800}>Industrial Personnel Enrollment</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Complete all mandatory fields to authorize new staff registry</Typography>
          </Box>
          <IconButton onClick={() => setOpenOnboardDialog(false)} sx={{ color: 'white' }}>
            <AddIcon sx={{ transform: 'rotate(45deg)' }} />
          </IconButton>
        </DialogTitle>
        <StaffForm 
          onSubmit={handleOnboard} 
          data={onboardData} 
          setData={setOnboardData} 
          onCancel={() => setOpenOnboardDialog(false)} 
          positions={positions}
          submitLabel="Authorize Personnel Enrollment"
        />
      </Dialog>

      {/* 1.1 EDIT STAFF DIALOG */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '24px', maxHeight: '90vh' } }}>
        <DialogTitle sx={{ px: 4, py: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #E2E8F0', background: 'linear-gradient(135deg, #1a237e 0%, #263b7c 100%)', color: 'white' }}>
          <Box>
            <Typography variant="h5" fontWeight={800}>Modify Personnel Profile</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Update registry details for {editData.name}</Typography>
          </Box>
          <IconButton onClick={() => setOpenEditDialog(false)} sx={{ color: 'white' }}>
            <AddIcon sx={{ transform: 'rotate(45deg)' }} />
          </IconButton>
        </DialogTitle>
        <StaffForm 
          onSubmit={handleUpdateStaff} 
          data={editData} 
          setData={setEditData} 
          onCancel={() => setOpenEditDialog(false)} 
          positions={positions}
          submitLabel="Update Registry Details"
          isEdit={true}
          onResendWelcome={() => handleResendWelcome(editingStaffId)}
        />
      </Dialog>

      {/* 2. GRANT ACCESS DIALOG */}
      <Dialog open={openGrantDialog} onClose={() => setOpenGrantDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' }}}>
          <DialogTitle sx={{ fontWeight: 900, borderBottom: '1px solid #E2E8F0', pb: 2 }}>Upgrade to System User</DialogTitle>
          <form onSubmit={handleGrantAccessFinal}>
              <DialogContent sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary" mb={3}>Assign system credentials for personnel.</Typography>
                  <TextField fullWidth label="Work Email" type="email" value={grantData.email} onChange={(e)=>setGrantData({...grantData, email: e.target.value})} required sx={{ mb: 2 }} />
                  <FormControl fullWidth>
                      <InputLabel>System Role</InputLabel>
                      <Select value={grantData.systemRole} label="System Role" onChange={(e)=>setGrantData({...grantData, systemRole: e.target.value})}>
                          <MenuItem value="STAFF">Standard Staff</MenuItem>
                          <MenuItem value="TEACHER">Teacher</MenuItem>
                      </Select>
                  </FormControl>
              </DialogContent>
              <DialogActions sx={{ p: 3, borderTop: '1px solid #E2E8F0' }}>
                  <Button onClick={()=>setOpenGrantDialog(false)}>Cancel</Button>
                  <Button type="submit" variant="contained" color="primary">Authorize Logic</Button>
              </DialogActions>
          </form>
      </Dialog>

      {/* 4. RECORD LEAVE DIALOG */}
      <Dialog open={openLeaveRecordDialog} onClose={() => setOpenLeaveRecordDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
        <DialogTitle sx={{ fontWeight: 900, borderBottom: '1px solid #E2E8F0' }}>Record Official Leave Request</DialogTitle>
        <form onSubmit={handleRecordLeaveSubmit}>
          <DialogContent sx={{ py: 4 }}>
            <Stack spacing={3}>
                <FormControl fullWidth>
                    <InputLabel>Select Staff Member</InputLabel>
                    <Select 
                      value={leaveRecordData.staffId} 
                      label="Select Staff Member" 
                      onChange={(e) => setLeaveRecordData({...leaveRecordData, staffId: e.target.value})} 
                      required
                    >
                        {staff.map(s => <MenuItem key={s.id} value={s.id}>{s.user?.name || s.name} ({s.employeeNo})</MenuItem>)}
                    </Select>
                </FormControl>

                <FormControl fullWidth>
                    <InputLabel>Leave Category</InputLabel>
                    <Select 
                      value={leaveRecordData.leaveType} 
                      label="Leave Category" 
                      onChange={(e) => setLeaveRecordData({...leaveRecordData, leaveType: e.target.value})} 
                      required
                    >
                        <MenuItem value="ANNUAL">Annual Leave (Statutory)</MenuItem>
                        <MenuItem value="SICK">Sick Leave (Medical Cert)</MenuItem>
                        <MenuItem value="SPECIAL">Special Leave (Compassionate)</MenuItem>
                        <MenuItem value="MATERNITY">Maternity / Paternity Leave</MenuItem>
                    </Select>
                </FormControl>

                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField fullWidth label="Start Date" type="date" value={leaveRecordData.startDate} onChange={(e) => setLeaveRecordData({...leaveRecordData, startDate: e.target.value})} InputLabelProps={{ shrink: true }} required />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField fullWidth label="End Date" type="date" value={leaveRecordData.endDate} onChange={(e) => setLeaveRecordData({...leaveRecordData, endDate: e.target.value})} InputLabelProps={{ shrink: true }} required />
                    </Grid>
                </Grid>

                <TextField fullWidth label="Total Days Count" type="number" value={leaveRecordData.daysTaken} onChange={(e) => setLeaveRecordData({...leaveRecordData, daysTaken: parseFloat(e.target.value)})} required />
                <TextField fullWidth label="Official Reason / Notes" multiline rows={3} value={leaveRecordData.reason} onChange={(e) => setLeaveRecordData({...leaveRecordData, reason: e.target.value})} placeholder="Provide brief context for the leave period..." />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 3, borderTop: '1px solid #E2E8F0', gap: 2 }}>
            <Button onClick={() => setOpenLeaveRecordDialog(false)} sx={{ fontWeight: 700, color: 'text.secondary' }}>Discard</Button>
            <Button type="submit" variant="contained" disabled={loadingAction} sx={{ borderRadius: '10px', fontWeight: 800, px: 4, bgcolor: '#1a237e' }}>Record Leave Entry</Button>
          </DialogActions>
        </form>
      </Dialog>

    </Box>
  );
};

const StaffForm = ({ onSubmit, data, setData, onCancel, positions, submitLabel, isEdit = false, onResendWelcome }) => (
  <form onSubmit={onSubmit}>
    <DialogContent sx={{ px: 4, py: 4, bgcolor: '#F8FAFC' }}>
       
       {/* SECTION 1: VITAL BIODATA */}
       <Paper elevation={0} sx={{ p: 4, borderRadius: '20px', border: '1px solid #E2E8F0', mb: 4, bgcolor: 'white' }}>
          <Typography variant="subtitle2" fontWeight={900} color="#1a237e" mb={3} display="flex" alignItems="center" gap={1.5}>
            <BadgeIcon fontSize="small" /> 1. VITAL BIODATA & CONTACT
          </Typography>
          
          <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Full Personnel Name" value={data.name} onChange={(e) => setData({...data, name: e.target.value})} required variant="outlined" placeholder="e.g. John Doe" />
              </Grid>
              <Grid item xs={12} md={3}>
                  <TextField fullWidth label="National ID" value={data.nationalId} onChange={(e) => setData({...data, nationalId: e.target.value})} placeholder="ID Number" />
              </Grid>
              <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                      <InputLabel>Gender Identity</InputLabel>
                      <Select value={data.gender} label="Gender Identity" onChange={(e) => setData({...data, gender: e.target.value})}>
                          <MenuItem value="MALE">Male</MenuItem>
                          <MenuItem value="FEMALE">Female</MenuItem>
                          <MenuItem value="OTHER">Other</MenuItem>
                      </Select>
                  </FormControl>
              </Grid>
              
              <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Personal Phone" value={data.contactNumber} onChange={(e) => setData({...data, contactNumber: e.target.value})} placeholder="+263..." />
              </Grid>
              <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Private Email Address" type="email" value={data.personalEmail} onChange={(e) => setData({...data, personalEmail: e.target.value})} required placeholder="email@example.com" />
              </Grid>
              <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Residential Address" value={data.address} onChange={(e) => setData({...data, address: e.target.value})} placeholder="House No, Street, City" />
              </Grid>
          </Grid>
       </Paper>

       {/* SECTION 2: SYSTEM AUTHENTICATION */}
       <Paper 
          elevation={0} 
          sx={{ 
              p: 4, 
              borderRadius: '20px', 
              border: '1px solid', 
              borderColor: data.grantAccess ? '#3182CE' : '#E2E8F0', 
              mb: 4, 
              bgcolor: data.grantAccess ? '#EBF8FF' : 'white',
              transition: 'all 0.3s ease'
          }}
       >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={data.grantAccess ? 3 : 0}>
              <Box>
                  <Typography variant="subtitle2" fontWeight={900} color={data.grantAccess ? '#2B6CB0' : '#4A5568'} display="flex" alignItems="center" gap={1.5}>
                      <VpnKeyIcon fontSize="small" /> 2. SYSTEM AUTHENTICATION (IT DEPT)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isEdit ? 'System credentials cannot be changed once assigned via HR.' : 'Automatically provision an account and send a secure access link'}
                  </Typography>
              </Box>
              {!isEdit && (
                <Switch 
                    checked={data.grantAccess} 
                    onChange={(e) => setData({...data, grantAccess: e.target.checked})}
                    color="primary"
                />
              )}
          </Box>

          {data.grantAccess && (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                      <TextField 
                          fullWidth 
                          label="Official Institution Email" 
                          type="email" 
                          value={data.email} 
                          disabled={isEdit}
                          onChange={(e) => setData({...data, email: e.target.value})} 
                          required 
                          sx={{ bgcolor: isEdit ? '#EDF2F7' : 'white' }}
                      />
                  </Grid>
                  <Grid item xs={12} md={6}>
                      <FormControl fullWidth sx={{ bgcolor: isEdit ? '#EDF2F7' : 'white' }}>
                          <InputLabel>Primary System Role</InputLabel>
                          <Select value={data.systemRole} label="Primary System Role" disabled={isEdit} onChange={(e) => setData({...data, systemRole: e.target.value})}>
                              <MenuItem value="STAFF">General Staff (Read-Only)</MenuItem>
                              <MenuItem value="TEACHER">Academic Faculty (Teacher)</MenuItem>
                              <MenuItem value="RECEPTIONIST">Front Desk (Reception)</MenuItem>
                              <MenuItem value="PRINCIPAL">Executive Admin (Principal)</MenuItem>
                          </Select>
                      </FormControl>
                  </Grid>
              </Grid>
          )}
       </Paper>

        {/* SECTION 2.5: COMPENSATION PARAMS */}
        <Paper elevation={0} sx={{ p: 4, borderRadius: '20px', border: '1px solid #E2E8F0', mb: 4, bgcolor: 'white' }}>
            <Typography variant="subtitle2" fontWeight={900} color="#1a237e" mb={3} display="flex" alignItems="center" gap={1.5}>
                <PaymentIcon fontSize="small" /> 2.5 COMPENSATION & PAYROLL PREFERENCE
            </Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                        <InputLabel>Preferred Payment Method</InputLabel>
                        <Select value={data.preferredPaymentMethod} label="Preferred Payment Method" onChange={(e) => setData({...data, preferredPaymentMethod: e.target.value})}>
                            <MenuItem value="CASH">Cash Payment</MenuItem>
                            <MenuItem value="BANK">Bank Transfer</MenuItem>
                            <MenuItem value="ECOCASH">EcoCash Wallet</MenuItem>
                            <MenuItem value="ONEMONEY">OneMoney Wallet</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                
                {data.preferredPaymentMethod === 'BANK' && (
                    <>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth label="Bank Name" value={data.bankName} onChange={(e) => setData({...data, bankName: e.target.value})} placeholder="e.g. CBZ, Stanbic, FBC" />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField fullWidth label="Account Number" value={data.bankAccount} onChange={(e) => setData({...data, bankAccount: e.target.value})} placeholder="Acc Number" />
                        </Grid>
                    </>
                )}

                {(data.preferredPaymentMethod === 'ECOCASH' || data.preferredPaymentMethod === 'ONEMONEY') && (
                    <Grid item xs={12} md={8}>
                        <TextField fullWidth label="Mobile Wallet Number" value={data.mobileWalletNo} onChange={(e) => setData({...data, mobileWalletNo: e.target.value})} placeholder="077... / 071..." />
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">This method and details will be prioritized during monthly salary processing batch runs.</Typography>
                </Grid>
            </Grid>
        </Paper>

        {/* SECTION 2.6: LEAVE ENTITLEMENTS */}
        <Paper elevation={0} sx={{ p: 4, borderRadius: '20px', border: '1px solid #E2E8F0', mb: 4, bgcolor: 'white' }}>
            <Typography variant="subtitle2" fontWeight={900} color="#1a237e" mb={3} display="flex" alignItems="center" gap={1.5}>
                <LeaveIcon fontSize="small" /> 2.6 LEAVE ENTITLEMENTS (DAYS BALANCE)
            </Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                    <TextField fullWidth type="number" label="Annual Leave Bal" value={data.annualLeaveBalance} onChange={(e) => setData({...data, annualLeaveBalance: e.target.value})} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField fullWidth type="number" label="Sick Leave (F) Bal" value={data.sickLeaveFullBalance} onChange={(e) => setData({...data, sickLeaveFullBalance: e.target.value})} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField fullWidth type="number" label="Sick Leave (H) Bal" value={data.sickLeaveHalfBalance} onChange={(e) => setData({...data, sickLeaveHalfBalance: e.target.value})} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <TextField fullWidth type="number" label="Special Leave Bal" value={data.specialLeaveBalance} onChange={(e) => setData({...data, specialLeaveBalance: e.target.value})} />
                </Grid>
                {data.gender === 'FEMALE' && (
                    <Grid item xs={12} md={3}>
                        <TextField fullWidth type="number" label="Maternity Bal" value={data.maternityLeaveBalance} onChange={(e) => setData({...data, maternityLeaveBalance: e.target.value})} />
                    </Grid>
                )}
            </Grid>
            <Box mt={2}>
              <Typography variant="caption" color="text.secondary">Balances specified here represent current legal entitlements. Use decimals for partial days.</Typography>
            </Box>
        </Paper>

       {/* SECTION 3: EMPLOYMENT CONTRACT */}
       <Paper elevation={0} sx={{ p: 4, borderRadius: '20px', border: '1px solid #E2E8F0', bgcolor: 'white' }}>
          <Typography variant="subtitle2" fontWeight={900} color="#1a237e" mb={3} display="flex" alignItems="center" gap={1.5}>
            <CheckCircleIcon fontSize="small" /> 3. EMPLOYMENT CONTRACT
          </Typography>
          <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                   <Autocomplete
                      freeSolo
                      openOnFocus
                      forcePopupIcon={true}
                      options={positions}
                      value={data.position}
                      onChange={(event, newValue) => setData({...data, position: newValue || ''})}
                      onInputChange={(event, newInputValue) => setData({...data, position: newInputValue})}
                      renderInput={(params) => (
                          <TextField 
                              {...params} 
                              label="Job Title (Suggested or Custom)" 
                              placeholder="Start typing..."
                              sx={{ 
                                  '& .MuiInputBase-root': {
                                      fontSize: '1rem',
                                      fontWeight: 600
                                  }
                              }} 
                          />
                      )}
                  />
              </Grid>
              <Grid item xs={12} md={4}>
                  <TextField 
                      fullWidth 
                      label="Commencement Date" 
                      type="date" 
                      value={data.dateOfJoining} 
                      onChange={(e) => setData({...data, dateOfJoining: e.target.value})} 
                      InputLabelProps={{ shrink: true }} 
                  />
              </Grid>
              <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                      <InputLabel>Department Designation</InputLabel>
                      <Select value={data.department} label="Department Designation" onChange={(e) => setData({...data, department: e.target.value})}>
                          <MenuItem value="Academic">Academic</MenuItem>
                          <MenuItem value="Admin">Administration</MenuItem>
                          <MenuItem value="Grounds">Grounds & Maintenance</MenuItem>
                          <MenuItem value="IT">IT Support & Services</MenuItem>
                      </Select>
                  </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                      <InputLabel>Nature of Contract</InputLabel>
                      <Select value={data.contractType} label="Nature of Contract" onChange={(e) => setData({...data, contractType: e.target.value})}>
                          <MenuItem value="PERMANENT">Full-Time Permanent</MenuItem>
                          <MenuItem value="CONTRACT">Fixed-Term Contract</MenuItem>
                          <MenuItem value="RELIEF">Relief / Temporary</MenuItem>
                          <MenuItem value="INTERN">Internship / Student teacher</MenuItem>
                      </Select>
                  </FormControl>
              </Grid>
          </Grid>
       </Paper>
    </DialogContent>
    <DialogActions sx={{ px: 4, py: 3, borderTop: '1px solid #E2E8F0', bgcolor: 'white', gap: 2 }}>
      {isEdit && (
          <Button 
            onClick={onResendWelcome}
            startIcon={<RefreshIcon />}
            sx={{ 
                mr: 'auto', 
                fontWeight: 700, 
                color: '#4C51BF',
                textTransform: 'none'
            }}
          >
              Resend Welcome Pack
          </Button>
      )}
      <Button 
          onClick={onCancel} 
          sx={{ 
              fontWeight: 700, 
              color: 'text.secondary',
              textTransform: 'none',
              fontSize: '1rem'
          }}
      >
          Discard Changes
      </Button>
      <Button 
          type="submit" 
          variant="contained" 
          sx={{ 
              borderRadius: '12px', 
              fontWeight: 800, 
              px: 5, 
              py: 1.5,
              bgcolor: '#1a237e',
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: '0 4px 14px 0 rgba(26, 35, 126, 0.39)',
              '&:hover': {
                  bgcolor: '#263b7c',
                  boxShadow: '0 6px 20px rgba(26, 35, 126, 0.23)'
              }
          }}
      >
          {submitLabel}
      </Button>
    </DialogActions>
  </form>
);

export default HRPage;
